/**
 * Per-window localStorage scoping — the SINGLE implementation shared by every loadboard (Load Hub, Box Truck).
 *
 * Two monitors showing the same board share one localStorage, so a value written under a plain key is seen by
 * every window (on reload, and live via the `storage` event). To keep each window independent we namespace the
 * value by this window's id (see ./windowId.ts): each window reads/writes `<baseKey>::<windowId>`. The bare key
 * is written as a mirror and read ONLY to seed a freshly-opened window — which also gives pre-existing users a
 * seamless migration (their bare value seeds the first window that opens after the change ships).
 *
 * Blobs left by windows that stopped writing are GC'd opportunistically on the next write. Each write records
 * its retention; callers that omit it use the 24h default.
 */

import { safeStorageOperation } from './localStorage'
import { getLoadHubWindowId } from './windowId'

const REGISTRY_KEY = 'admin_per_window_registry'
const SCOPED_SESSION_MIRROR_PREFIX = 'admin_per_window_scoped:'
const TTL_MS = 24 * 60 * 60 * 1000 // 1 day
// Old registry entries did not record a TTL. Give them the longest existing workspace lifetime during
// migration so an unrelated window's write cannot discard a saved workspace before its owner opens it.
const LEGACY_REGISTRY_TTL_MS = 7 * TTL_MS

/** Legacy registry entries are timestamps; new entries also carry the caller's retention. */
type RegistryEntry = number | { touchedAt: number; ttlMs: number }
type Registry = Record<string, RegistryEntry>

const readRegistry = (): Registry => {
    const stored = localStorage.getItem(REGISTRY_KEY)
    return stored ? JSON.parse(stored) : {}
}

/** This window's namespaced variant of a base key. Exported so callers (e.g. useStorageSync) can scope their
 *  own `storage`-event matching to this window's keys. */
export const windowScopedKey = (baseKey: string): string => `${baseKey}::${getLoadHubWindowId()}`

/** Read this window's value for `baseKey`; fall back to the bare key so a freshly-opened window seeds from the
 *  last-saved state (and pre-existing users migrate seamlessly). */
export const readPerWindow = (baseKey: string): string | null =>
    safeStorageOperation(
        () => {
            const own = localStorage.getItem(windowScopedKey(baseKey))
            return own !== null ? own : localStorage.getItem(baseKey)
        },
        null,
        'readPerWindow'
    )

/** Read only this window's namespaced value. Dedicated/isolated windows use this to avoid seeding from the
 *  shared bare mirror when they intentionally represent a strict subset of another window's workspace. */
export const readPerWindowScoped = (baseKey: string): string | null =>
    safeStorageOperation(
        () => localStorage.getItem(windowScopedKey(baseKey)) ?? sessionStorage.getItem(`${SCOPED_SESSION_MIRROR_PREFIX}${baseKey}`),
        null,
        'readPerWindowScoped'
    )

const writeWindowValue = (baseKey: string, value: string, mirror: boolean, operation: string, ttlMs: number): void =>
    safeStorageOperation(
        () => {
            const key = windowScopedKey(baseKey)
            localStorage.setItem(key, value)
            if (mirror) localStorage.setItem(baseKey, value)
            const registry = readRegistry()
            const now = Date.now()
            registry[key] = { touchedAt: now, ttlMs }
            // GC blobs no window has written within their own TTL. Never evict THIS window's own keys:
            // a window left open but idle beyond its TTL must keep its board.
            const ownSuffix = `::${getLoadHubWindowId()}`
            for (const k of Object.keys(registry)) {
                const entry = registry[k]
                const touchedAt = typeof entry === 'number' ? entry : entry.touchedAt
                const retention = typeof entry === 'number' ? LEGACY_REGISTRY_TTL_MS : entry.ttlMs
                if (k !== key && !k.endsWith(ownSuffix) && now - touchedAt > retention) {
                    localStorage.removeItem(k)
                    delete registry[k]
                }
            }
            localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry))
        },
        undefined,
        operation
    )

/** Write to this window's namespaced key AND mirror to the bare key (the seed source for future windows). An
 *  already-open window never reads the bare key, so another window clobbering that mirror can't disturb it. */
export const writePerWindow = (baseKey: string, value: string, ttlMs = TTL_MS): void => writeWindowValue(baseKey, value, true, 'writePerWindow', ttlMs)

/** Write only this window's namespace, without replacing the bare seed inherited by future windows. */
export const writePerWindowScoped = (baseKey: string, value: string, ttlMs = TTL_MS): void => {
    writeWindowValue(baseKey, value, false, 'writePerWindowScoped', ttlMs)
    // Chrome copies sessionStorage when a tab is duplicated. Keep an isolated-only seed there so the copy can
    // restore the detached workspace after windowId correctly forks its localStorage namespace. Normal windows
    // never read this mirror, and detached windows still never touch the shared bare-key seed.
    try {
        sessionStorage.setItem(`${SCOPED_SESSION_MIRROR_PREFIX}${baseKey}`, value)
    } catch {
        // Blocked sessionStorage only disables duplicate-tab restoration; scoped localStorage remains usable.
    }
}
